import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// A slim strip that appears above ClubHeader when:
//   * paywall is enabled,
//   * the current club's plan_tier is "trial" or "expired",
//   * OR they're in the soft-locked grace period after a payment failure.
// Dismissing hides it for the browser session only — it reappears on
// next login so a stale trial reminder can't be missed for weeks.
// Superadmins and public/unauthenticated views never see this.
export default function TrialBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only ADMIN sees the banner — members can't act on it anyway.
    if (!user || user.role !== "ADMIN") return;
    // sessionStorage flag = one-off dismissal until next login/tab.
    if (sessionStorage.getItem("ccs_trial_banner_dismissed") === "1") {
      setDismissed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (!cancelled) setStatus(data);
      } catch {
        // Silently no-op — worst case the banner just doesn't show.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!status || dismissed) return null;
  if (!status.paywall_enabled) return null;
  const tier = status.plan_tier;
  // grandfathered / active starter/unlimited → nothing to say
  if (tier === "grandfathered") return null;
  if (["starter", "unlimited"].includes(tier) && status.subscription_status === "active" && !status.is_soft_locked) {
    return null;
  }

  const days = status.days_remaining;
  let message = "";
  let tone = "amber"; // amber | red

  if (tier === "trial") {
    if (days === null || days === undefined) {
      message = "You're on a free trial.";
    } else if (days <= 0) {
      message = "Your free trial has ended — upgrade to keep making changes.";
      tone = "red";
    } else if (days <= 3) {
      message = `Your free trial ends in ${days} day${days === 1 ? "" : "s"}.`;
      tone = "red";
    } else {
      message = `${days} day${days === 1 ? "" : "s"} left on your free trial.`;
    }
  } else if (status.is_soft_locked) {
    message = "Your subscription needs attention — update your card to keep your data editable.";
    tone = "red";
  } else if (tier === "expired") {
    message = "Your plan has ended. Renew to make changes again.";
    tone = "red";
  } else {
    return null;
  }

  const bg = tone === "red" ? "rgba(239,68,68,0.12)" : "rgba(251,191,36,0.10)";
  const border = tone === "red" ? "rgba(239,68,68,0.35)" : "rgba(251,191,36,0.35)";
  const accent = tone === "red" ? "text-red-400" : "text-amber-400";

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-2 text-sm border-b"
      style={{ background: bg, borderColor: border }}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Clock size={14} className={accent + " shrink-0"} />
        <span className="text-neutral-200 truncate">{message}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Link
          to="/billing"
          className={"text-sm underline underline-offset-2 " + accent}
          data-testid="trial-banner-upgrade"
        >
          {tier === "trial" ? "Upgrade now" : "Manage billing"}
        </Link>
        <button
          onClick={() => {
            sessionStorage.setItem("ccs_trial_banner_dismissed", "1");
            setDismissed(true);
          }}
          className="text-neutral-500 hover:text-white"
          title="Hide for this session"
          data-testid="trial-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
